import { useMerchantCabinetConfig } from '../../config/ConfigProvider';
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { assert, groupByPredicate } from '@payler/utils';
import dayjs from 'dayjs';
import { PaginationProps } from '@payler/ui-components';
import {
  TCreatePayoutRequest,
  TCreateReportPayloadV2,
  TUploadBulkPayoutFileRequestBody,
  TUploadBulkRefundFileRequestBody,
} from '../../../../../api/src';
import { useCallback } from 'react';
import createLogger from 'debug';
import { usePayoutsFiltersFromUrl } from './url';
import { usePagerPropsFromUrl } from '../pager/use-pager-props-from-url';
import { usePagerUrlCallbacks } from '../pager/use-pager-url-callbacks';
import { useDropReportsCache } from '../reports/cache';
import { DEFAULT_PAGER_PROPS, NEW_REPORT_CREDIT_STATUSES } from '../../const';
import { useCurrencyCallbacks } from '../currencies-info';
import { useUIDateFormat } from '../use-ui-dateFormat';
import { useCurrentDate, usePreviousDate } from '../use-previous-date';
import { useURLContractId } from '../../layouts/ContractIdLayout/ContractIdLayout';
import { useApi } from '../../context/ApiContextProvider';
import { useSelectedContract } from '../contracts';
import { TPayoutsFiltersForm } from '../../forms/PaymentsFilters/types';
import { useBulkOperationsPeriodFromUrl } from './bulk-period';
import { captureHookError } from '../../utils/captureHookError';

const log = createLogger('hooks:payments:queries');

const PAYOUTS_STALE_TIME = 60 * 1000;
const PAYOUTS_TEMPLATES_STALE_TIME = 600 * 1000;

type TUsePayoutsQueryOptions = {
  page?: number;
  bulkPayoutId?: string;
  bulkRefundId?: string;
  bulkCreatedAt?: string;
};

export const usePayoutsQuery = (
  context: 'payouts' | 'withdrawals',
  options?: TUsePayoutsQueryOptions,
) => {
  const api = useApi();
  const { culture, environment } = useMerchantCabinetConfig();
  const contractId = useURLContractId();
  const pagerFromUrl = usePagerPropsFromUrl();
  const pagerData = {
    ...pagerFromUrl,
    Page: options?.page ?? pagerFromUrl.Page,
  };
  const urlFilters = usePayoutsFiltersFromUrl();
  const { isBulkPayoutAllowed } = useSelectedContract() || {};
  const previousDate = usePreviousDate();
  const currentDate = useCurrentDate();
  const { bulkPayoutId, bulkRefundId } = options ?? {};
  let filters =
    context === 'payouts'
      ? urlFilters
      : {
          StartDate: previousDate,
          ...(environment === 'prod' ? { EndDate: currentDate } : {}),
        };

  const bulkOperationFilter =
    context === 'withdrawals'
      ? bulkRefundId
        ? { BulkRefundId: bulkRefundId }
        : bulkPayoutId
          ? { BulkPayoutId: bulkPayoutId }
          : undefined
      : undefined;

  if (bulkOperationFilter) {
    // For bulk operation details we use dedicated /operations endpoints (no period required)
    filters = { PayoutsType: 'All', ...bulkOperationFilter };
  }

  if (context === 'payouts') {
    if (isBulkPayoutAllowed) {
      filters = {
        ...filters,
        PayoutsType:
          (filters as TPayoutsFiltersForm).PayoutsType ?? 'All',
      };
    } else if ('PayoutsType' in filters) {
      const { PayoutsType, ...rest } = filters;
      filters = rest;
    }
  }

  const requestFilters = { ...(filters as Record<string, unknown>) };
  delete requestFilters.IncludeBulkPayouts;

  if (!contractId) throw new Error('no contractId');
  const hasBulkFilter = !!(bulkPayoutId || bulkRefundId);
  return useQuery({
    queryKey: [
      'payouts',
      culture,
      contractId,
      pagerData,
      filters,
      bulkPayoutId,
      bulkRefundId,
    ],
    queryFn: async () => {
      const data = hasBulkFilter
        ? bulkRefundId
          ? await api.getBulkPayoutRefundOperations({
              bulkRefundId,
              contractId,
              page: pagerData.Page,
              pageSize: pagerData.PageSize,
            })
          : await api.getBulkPayoutOperations({
              bulkPayoutId: bulkPayoutId as string,
              contractId,
              page: pagerData.Page,
              pageSize: pagerData.PageSize,
            })
        : await api.getPayouts({
            ContractId: contractId,
            ...(requestFilters as TPayoutsFiltersForm),
            ...pagerData,
          });

      const grouped = groupByPredicate(data.items, (v) => {
        const dateString = v['firstOperationDate'] as string;
        return dayjs(dateString).format('YYYY-MM-DD');
      });
      return { ...data, grouped };
    },
    staleTime: !hasBulkFilter ? PAYOUTS_STALE_TIME : undefined,
    refetchOnWindowFocus: hasBulkFilter ? true : undefined,
    refetchOnMount: hasBulkFilter ? true : undefined,
    refetchOnReconnect: true,
  });
};

export const usePayoutsQueryPagerProps = (
  context: 'payouts' | 'withdrawals',
  options?: TUsePayoutsQueryOptions,
) => {
  const payments = usePayoutsQuery(context, options);
  const { goToPage, setPageSize } = usePagerUrlCallbacks();
  if (payments.status !== 'success') {
    return { ...DEFAULT_PAGER_PROPS, goToPage, setPageSize } as PaginationProps;
  }
  const { page, pageSize, totalItems } = payments.data.paginationMetadata;
  return {
    goToPage,
    setPageSize,
    pageSize,
    currentPage: page,
    totalRecords: totalItems,
  } as PaginationProps;
};

export const usePayoutsTemplatesQuery = () => {
  const api = useApi();
  const contractId = useURLContractId();
  if (!contractId) throw new Error('no contractId');
  return useSuspenseQuery({
    queryKey: ['payouts', 'templates', contractId],
    queryFn: async () => api.getPayoutsTemplates(contractId),
    staleTime: PAYOUTS_TEMPLATES_STALE_TIME,
  });
};

export const useSavePayoutsFiltersToReport = () => {
  const dropReportsCache = useDropReportsCache();
  const filters = usePayoutsFiltersFromUrl();
  const api = useApi();
  const contractId = useURLContractId();
  const dateFormat = useUIDateFormat();
  const { isBulkPayoutAllowed } = useSelectedContract() || {};
  return useCallback(
    async (name: string) => {
      try {
        if (!contractId) return;
        const data: TCreateReportPayloadV2 = {
          name,
          contractIds: [contractId],
          currencyCodes: filters.CurrencyCodes ?? [],
          transactionStatuses: filters.TransactionStatuses?.length
            ? filters.TransactionStatuses
            : NEW_REPORT_CREDIT_STATUSES,
          startDate: filters.StartDate,
          endDate: filters.EndDate,
          startAmount: filters.StartAmount,
          endAmount: filters.EndAmount,
          CardLast4: filters.CardLast4,
          Userdata: filters.Userdata,
          RRN: filters.RRN,
          OrderId: filters.OrderId ?? undefined,
          direction: 'payouts',
        };

        if (isBulkPayoutAllowed) {
          data.PayoutsType = (filters.PayoutsType || 'All') as 'Single' | 'All' | 'Bulk';
        }

        await api.createReportV2(data, dateFormat);
        dropReportsCache();
      } catch (e) {
        log('useSavePayoutsFiltersToReport error: %O', e);
        captureHookError(e, 'useSavePayoutsFiltersToReport');
      }
    },
    [contractId, api, dropReportsCache, filters, dateFormat, isBulkPayoutAllowed],
  );
};

/**
 * рефрешим страницы сделать выплату и выплаты/список и переходим на первую страницу
 */
export const useDropPayoutsQueryCache = (
  context: Parameters<typeof usePayoutsQuery>[0],
) => {
  const client = useQueryClient();
  const { goToPage } = usePayoutsQueryPagerProps(context);
  return useCallback(() => {
    client.invalidateQueries({ queryKey: ['payouts'] }).then(() => goToPage(1));
  }, [client, goToPage]);
};

export const useMakeCreditMutation = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { getCurrencyUnitRate } = useCurrencyCallbacks();
  return useMutation({
    mutationFn: (data: TCreatePayoutRequest) =>
      api.createPayout(data, getCurrencyUnitRate(data.currency)),

    onSuccess: (_, { contractId }) => {
      queryClient
        .refetchQueries({ queryKey: ['balances', contractId] })
        .catch((e) => {
          log('failed to refetch balance');
          captureHookError(e, 'useMakeCreditMutation.refetchQueries(balances)');
        });
    },
  });
};

export const useBulkPayoutEncryptionKeyQuery = () => {
  const { isBulkPayoutAllowed } = useSelectedContract() || {};
  const api = useApi();
  const contractId = useURLContractId();
  assert(typeof contractId === 'number', 'Contract id must be number');

  return useSuspenseQuery({
    queryKey: ['bulkPayoutEncryptionKey', contractId],
    queryFn: async () =>
      isBulkPayoutAllowed ? api.getBulkPayoutEncryptionKey(contractId) : null,
    staleTime: PAYOUTS_STALE_TIME,
    refetchOnWindowFocus: false,
  });
};

export const useBulkPayoutsQuery = () => {
  const api = useApi();
  const contractId = useURLContractId();
  const pagerData = usePagerPropsFromUrl();
  const { isBulkPayoutAllowed } = useSelectedContract() || {};
  const { StartDate, EndDate } = useBulkOperationsPeriodFromUrl();

  assert(typeof contractId === 'number', 'Contract id must be number');

  return useQuery({
    queryKey: [
      'bulkPayouts',
      contractId,
      StartDate,
      EndDate,
      pagerData.Page,
      pagerData.PageSize,
    ],
    queryFn: async () =>
      api.getBulkPayouts({
        ContractId: contractId,
        StartDate,
        EndDate,
        ...pagerData,
      }),
    staleTime: PAYOUTS_STALE_TIME,
    refetchOnWindowFocus: false,
    enabled: !!isBulkPayoutAllowed,
  });
};

export const useResetBulkPayoutsQuery = () => {
  const contractId = useURLContractId();
  assert(typeof contractId === 'number', 'Contract id must be number');
  const client = useQueryClient();
  return useCallback(async () => {
    if (!client.isFetching({ queryKey: ['bulkPayouts', contractId] })) {
      return await client.resetQueries({
        queryKey: ['bulkPayouts', contractId],
      });
    }
  }, []);
};

export const useBulkPayoutsQueryPagerProps = () => {
  const payoutsQuery = useBulkPayoutsQuery();
  const { goToPage, setPageSize } = usePagerUrlCallbacks();
  if (payoutsQuery.status !== 'success') {
    return { ...DEFAULT_PAGER_PROPS, goToPage, setPageSize } as PaginationProps;
  }
  const { page, pageSize, totalItems } = payoutsQuery.data.paginationMetadata;
  return {
    goToPage,
    setPageSize,
    pageSize,
    currentPage: page,
    totalRecords: totalItems,
  } as PaginationProps;
};

export const useMakeBulkPayoutMutation = () => {
  const api = useApi();
  const contractId = useURLContractId();
  assert(typeof contractId === 'number', 'Contract id must be number');

  return useMutation({
    mutationFn: (data: Omit<TUploadBulkPayoutFileRequestBody, 'contractId'>) =>
      api.uploadBulkPayoutFile({ ...data, contractId }),
  });
};

export const useBulkPayoutDetailsQuery = (bulkPayoutId: string | undefined) => {
  const api = useApi();
  const client = useQueryClient();
  const contractId = useURLContractId();
  assert(typeof contractId === 'number', 'Contract id must be number');

  const data = useQuery({
    queryKey: ['bulkPayoutFileInfo', bulkPayoutId],
    queryFn: async () => {
      assert(!!bulkPayoutId, 'bulkPayoutId can not be empty');
      return await api.getBulkPayoutDetails(bulkPayoutId, contractId);
    },
    enabled: !!bulkPayoutId,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const clearBulkPayoutFileInfo = useCallback(async () => {
    if (
      !client.isFetching({ queryKey: ['bulkPayoutFileInfo', bulkPayoutId] })
    ) {
      return await client.invalidateQueries({
        queryKey: ['bulkPayoutFileInfo', bulkPayoutId],
      });
    }
  }, [bulkPayoutId]);

  return { ...data, clearBulkPayoutFileInfo };
};

export const useBulkPayoutActionsMutation = () => {
  const api = useApi();
  const contractId = useURLContractId();
  assert(typeof contractId === 'number', 'Contract id must be number');
  const startMutation = useMutation({
    mutationFn: (params: { bulkPayoutId: string; password: string }) =>
      api.startBulkPayout({ contractId, ...params }),
  });
  const cancelMutation = useMutation({
    mutationFn: (bulkPayoutId: string) =>
      api.cancelBulkPayout(bulkPayoutId, contractId),
  });

  return { startMutation, cancelMutation };
};

export const useDownloadBulkPayoutReport = (id: string) => {
  const api = useApi();
  const contractId = useURLContractId();

  return async () => {
    assert(!!contractId, 'Contract id must be number');
    await api.downloadBulkPayoutReport(id, contractId);
  };
};

export const useBulkRefundEncryptionKeyQuery = () => {
  const { isBulkRefundOverPayoutAllowed } = useSelectedContract() || {};
  const api = useApi();
  const contractId = useURLContractId();
  assert(typeof contractId === 'number', 'Contract id must be number');

  return useSuspenseQuery({
    queryKey: ['bulkRefundEncryptionKey', contractId],
    queryFn: async () =>
      isBulkRefundOverPayoutAllowed
        ? api.getBulkRefundEncryptionKey(contractId)
        : null,
    staleTime: PAYOUTS_STALE_TIME,
    refetchOnWindowFocus: false,
  });
};

export const useBulkRefundsQuery = () => {
  const api = useApi();
  const contractId = useURLContractId();
  const pagerData = usePagerPropsFromUrl();
  const { isBulkRefundOverPayoutAllowed } = useSelectedContract() || {};
  const { StartDate, EndDate } = useBulkOperationsPeriodFromUrl();

  assert(typeof contractId === 'number', 'Contract id must be number');

  return useQuery({
    queryKey: [
      'bulkRefunds',
      contractId,
      StartDate,
      EndDate,
      pagerData.Page,
      pagerData.PageSize,
    ],
    queryFn: async () =>
      api.getBulkRefunds({
        ContractId: contractId,
        StartDate,
        EndDate,
        ...pagerData,
      }),
    staleTime: PAYOUTS_STALE_TIME,
    refetchOnWindowFocus: false,
    enabled: !!isBulkRefundOverPayoutAllowed,
  });
};

export const useResetBulkRefundsQuery = () => {
  const contractId = useURLContractId();
  assert(typeof contractId === 'number', 'Contract id must be number');
  const client = useQueryClient();
  return useCallback(async () => {
    if (!client.isFetching({ queryKey: ['bulkRefunds', contractId] })) {
      return await client.resetQueries({
        queryKey: ['bulkRefunds', contractId],
      });
    }
  }, []);
};

export const useBulkRefundsQueryPagerProps = () => {
  const refundsQuery = useBulkRefundsQuery();
  const { goToPage, setPageSize } = usePagerUrlCallbacks();
  if (refundsQuery.status !== 'success') {
    return { ...DEFAULT_PAGER_PROPS, goToPage, setPageSize } as PaginationProps;
  }
  const { page, pageSize, totalItems } = refundsQuery.data.paginationMetadata;
  return {
    goToPage,
    setPageSize,
    pageSize,
    currentPage: page,
    totalRecords: totalItems,
  } as PaginationProps;
};

export const useMakeBulkRefundMutation = () => {
  const api = useApi();
  const contractId = useURLContractId();
  assert(typeof contractId === 'number', 'Contract id must be number');

  return useMutation({
    mutationFn: (data: Omit<TUploadBulkRefundFileRequestBody, 'contractId'>) =>
      api.uploadBulkRefundFile({ ...data, contractId }),
  });
};

export const useBulkRefundDetailsQuery = (bulkRefundId: string | undefined) => {
  const api = useApi();
  const client = useQueryClient();
  const contractId = useURLContractId();
  assert(typeof contractId === 'number', 'Contract id must be number');

  const data = useQuery({
    queryKey: ['bulkRefundFileInfo', bulkRefundId],
    queryFn: async () => {
      assert(!!bulkRefundId, 'bulkRefundId can not be empty');
      return await api.getBulkRefundDetails(bulkRefundId, contractId);
    },
    enabled: !!bulkRefundId,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const clearBulkRefundFileInfo = useCallback(async () => {
    if (
      !client.isFetching({ queryKey: ['bulkRefundFileInfo', bulkRefundId] })
    ) {
      return await client.invalidateQueries({
        queryKey: ['bulkRefundFileInfo', bulkRefundId],
      });
    }
  }, [bulkRefundId]);

  return { ...data, clearBulkRefundFileInfo };
};

export const useBulkRefundActionsMutation = () => {
  const api = useApi();
  const contractId = useURLContractId();
  assert(typeof contractId === 'number', 'Contract id must be number');
  const startMutation = useMutation({
    mutationFn: (params: { bulkRefundId: string; password: string }) =>
      api.startBulkRefund({ contractId, ...params }),
  });
  const cancelMutation = useMutation({
    mutationFn: (bulkRefundId: string) =>
      api.cancelBulkRefund(bulkRefundId, contractId),
  });

  return { startMutation, cancelMutation };
};

export const useDownloadBulkRefundReport = (id: string) => {
  const api = useApi();
  const contractId = useURLContractId();

  return async () => {
    assert(!!contractId, 'Contract id must be number');
    await api.downloadBulkRefundReport(id, contractId);
  };
};
